// src/components/CEOQuestionnaireModule.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc
} from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';
import {
    Paper,
    Typography,
    Button,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Chip,
    Alert,
    Tooltip
} from '@mui/material';
import { toast } from 'react-toastify';
import { app } from '../firebase';
import PublicQuestionnaire from './PublicQuestionnaire';

const functions = getFunctions(app, 'us-central1'); // ✅ Correct modular initialization
const CEOQuestionnaireModule = ({ clientId, clientData, user }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestResponse, setLatestResponse] = useState(null);
  const [assignedTemplateId, setAssignedTemplateId] = useState(null);
  const [assignedTemplateVersion, setAssignedTemplateVersion] = useState(null);
  const [isSendLinkModalOpen, setSendLinkModalOpen] = useState(false);
  const [ceoEmail, setCeoEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Questionnaire modal states
  const [questionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);
  const [questionnaireMode, setQuestionnaireMode] = useState('fill'); // 'fill', 'continue_draft', 'view'
  const [templateData, setTemplateData] = useState(null);
  const [initialResponseData, setInitialResponseData] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [questionnaireError, setQuestionnaireError] = useState(null);
  useEffect(() => {
  if (!clientId || !user?.id) {
    setLoading(false);
    return;
  }

  const templateId = user?.assignedQuestionnaireTemplateId;
  const templateVersion = user?.assignedQuestionnaireTemplateVersion;
  setAssignedTemplateId(templateId);
  setAssignedTemplateVersion(templateVersion);

  if (!templateId) {
    setLatestResponse(null);
    setLoading(false);
    return;
  }

  setLoading(true);
  setError(null);
  const db = getFirestore(app);

  // --- START: New "Waiter" Pattern Logic ---
  const clientDocRef = doc(db, 'clients', clientId);
  let unsubscribeResponses = null; // To hold the subcollection listener's unsub function

  // Stage 1: Listen to the parent client document first.
  const unsubscribeClient = onSnapshot(clientDocRef, (clientSnap) => {
    if (!clientSnap.exists()) {
      // The parent document doesn't exist yet, so we wait.
      console.log(`CEOQuestionnaireModule: Waiting for parent client document ${clientId} to be created...`);
      return;
    }

    const clientDocData = clientSnap.data();
    const isOwner = user?.id && (clientDocData.fcfoId === user.id || clientDocData.userId === user.id);

    // Stage 2: Once the parent exists and is owned by the user, attach the subcollection listener.
    if (isOwner && !unsubscribeResponses) {
      console.log(`CEOQuestionnaireModule: Parent client ${clientId} confirmed. Attaching questionnaire listener.`);

      const responsesRef = collection(db, 'clients', clientId, 'ceoQuestionnaireResponses');
      const q = query(
        responsesRef,
        where('questionnaireTemplateId', '==', templateId),
        orderBy('lastSavedAt', 'desc'),
        limit(1)
      );

      unsubscribeResponses = onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const responseDoc = querySnapshot.docs[0];
          setLatestResponse({ id: responseDoc.id, ...responseDoc.data() });
        } else {
          setLatestResponse(null);
        }
        setLoading(false);
      }, (err) => {
        console.error("Error with questionnaire subcollection listener:", err);
        setError("Failed to listen for questionnaire updates.");
        setLoading(false);
        toast.error("Connection to questionnaire status failed.");
      });
    } else if (!isOwner) {
        // This case handles if the listener somehow runs for a non-owner.
        setLoading(false);
        setError("You do not have permission to view this client's questionnaires.");
    }

  }, (err) => {
    console.error("Error with parent client listener:", err);
    setError("Failed to listen for client data.");
    setLoading(false);
  });

  // Cleanup function to detach BOTH listeners on unmount
  return () => {
    unsubscribeClient();
    if (unsubscribeResponses) {
      unsubscribeResponses();
    }
  };
  // --- END: New "Waiter" Pattern Logic ---

}, [clientId, user]);

  const handleOpenSendLinkModal = () => {
    setCeoEmail('');
    setCustomMessage('');
    setSendLinkModalOpen(true);
  };

  const handleSendLink = async () => {
    if (!ceoEmail) {
      toast.error("CEO Email Address is required.");
      return;
    }

    console.log(`CEOQuestionnaireModule: Initiating send link to CEO: ${ceoEmail} for client: ${clientId} template: ${assignedTemplateId} version: ${assignedTemplateVersion} customMessage: ${customMessage}`);

    setIsSending(true);

    try {
      const sendNativeQuestionnaireInvite = httpsCallable(functions, 'sendNativeQuestionnaireInvite');

      const payload = {
        clientId,
        questionnaireTemplateId: assignedTemplateId,
        templateVersion: assignedTemplateVersion,
        ceoEmail,
        customMessage
      };

      console.log("DEBUG: Payload being sent to function:", payload);

      const result = await sendNativeQuestionnaireInvite(payload);
      if (result.data.success) {
        toast.success("Questionnaire invitation sent successfully!");
        setSendLinkModalOpen(false);
        
        // Navigate back to Account tab if this was during initial setup
        if (clientData && clientData.isSetupComplete === false) {
          setTimeout(() => {
            navigate('/', { state: { targetTab: 3 } });
            toast.info('Redirecting to Account page to continue setup...');
          }, 1500);
        }
      } else {
        throw new Error(result.data.message || "Failed to send invitation.");
      }
    } catch (error) {
      console.error("Error sending questionnaire invite:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

// Handle questionnaire modal opening
const handleOpenQuestionnaire = async (mode) => {
  setQuestionnaireMode(mode);
  setQuestionnaireLoading(true);
  setQuestionnaireError(null);
  setQuestionnaireModalOpen(true);

  try {
    const db = getFirestore(app);
    
    // Fetch the assigned template
    const templateDoc = await getDoc(doc(db, 'questionnaireTemplates', assignedTemplateId));
    if (!templateDoc.exists()) {
      throw new Error('Questionnaire template not found');
    }
    setTemplateData(templateDoc.data());

    // If continuing draft or viewing, fetch the response data
if (mode === 'continue_draft' || mode === 'view') {
  if (latestResponse) {
    // Convert response array back to object format for the component
    const responseObj = {};
    if (latestResponse.responses && Array.isArray(latestResponse.responses)) {
      latestResponse.responses.forEach((resp) => {
        responseObj[resp.questionId] = resp.answer;
      });
    }
    setInitialResponseData(responseObj);
  }
} else {
  setInitialResponseData({});
}
  } catch (error) {
    console.error('Error loading questionnaire:', error);
    setQuestionnaireError(`Failed to load questionnaire: ${error.message}`);
    toast.error('Failed to load questionnaire');
  } finally {
    setQuestionnaireLoading(false);
  }
};

// Handle questionnaire submission
// Handle questionnaire submission
const handleQuestionnaireSubmit = async (responses, isSubmission = true) => {
  if (isSubmitting) return; // Prevent double clicks
  setIsSubmitting(true);      // Disable the button

  try {
    const db = getFirestore(app);
    // Convert responses object to array format for Firestore
    const responsesArray = Object.entries(responses).map(([key, answer]) => ({
      questionId: key,
      answer: answer,
    }));

    const isFinalSubmission = isSubmission && questionnaireMode !== 'view';
    const docStatus = isFinalSubmission ? 'submitted_pii_review_pending' : 'draft';

    const submissionData = {
      clientId: clientId,
      questionnaireTemplateId: assignedTemplateId,
      questionnaireTemplateVersion: templateData.version,
      responses: responsesArray,
      status: docStatus,
      userId: user.id,
      userEmail: user.email,
      lastSavedAt: new Date(),
    };

    if (isFinalSubmission) {
      submissionData.submittedAt = new Date();
    }

    // Save the detailed response to the subcollection
    const responsesRef = collection(db, 'clients', clientId, 'ceoQuestionnaireResponses');
    let savedResponseId;

    if (questionnaireMode === 'continue_draft' && latestResponse && !isFinalSubmission) {
      // If we are updating an existing draft
      const draftRef = doc(responsesRef, latestResponse.id);
      await updateDoc(draftRef, submissionData);
      savedResponseId = latestResponse.id;
      toast.success('Draft updated successfully!');
    } else {
      // If we are creating a new draft or a final submission
      const docRef = await addDoc(responsesRef, submissionData);
      savedResponseId = docRef.id;
      // --- START: New code block for metadata preparation ---
      if (isFinalSubmission) {
        console.log(`[DEBUG] Preparing to create metadata document for new response ID: ${savedResponseId}`);
        
        const docHistoryData = {
          clientId: clientId,
          userId: user.id,
          fcfoId: clientData?.fcfoId || null,
          userEmail: user.email,
          type: 'questionnaire_native',
          label: `${templateData.name || 'CEO Questionnaire'} - Submitted`,
          name: `Native Submission - ${savedResponseId}`,
          questionnaireTemplateId: assignedTemplateId,
          questionnaireTemplateVersion: templateData.version,
          nativeResponseId: savedResponseId,
          status: 'submitted_pii_review_pending',
          documentStatus: 'Awaiting Sanitization',
          versionNumber: 1,
          parentId: null,
          url: null,
          size: null,
          contentType: 'application/savvai-native-questionnaire',
          lastModifiedBy: user.email
        };
        
        // --- START: New code for secure backend call ---
        try {
          const createDocumentMetadata = httpsCallable(functions, 'createDocumentMetadata');
          await createDocumentMetadata({ docHistoryData });
          console.log(`[SUCCESS] Metadata document created successfully for response: ${savedResponseId}`);
        } catch (metadataError) {
          console.error("CRITICAL: Failed to create metadata document via Cloud Function.", metadataError);
          toast.error("Your questionnaire was submitted, but failed to appear in the document history. Please contact support.");
        }
        // --- END: New code for secure backend call ---
      }
      // --- END: New code block for metadata preparation ---
      toast.success(isFinalSubmission ? 'Questionnaire submitted successfully!' : 'Draft saved successfully!');
      
      // Navigate back to Account tab ONLY if this was a final submission during initial setup
if (isFinalSubmission && clientData && clientData.isSetupComplete === false) {
  setTimeout(() => {
    navigate('/', { state: { targetTab: 3 } });
    toast.info('Redirecting to Account page to continue setup...');
  }, 1500);
}
    }
    
    // Only close modal for final submissions, not drafts
    if (isFinalSubmission) {
      setQuestionnaireModalOpen(false);
    }

  } catch (error) {
    console.error('Error submitting questionnaire:', error);
    toast.error(`Failed to submit questionnaire: ${error.message}`);
  } finally {
    setIsSubmitting(false); // Re-enable the button when done
  }
};
// Handle modal close
const handleCloseQuestionnaire = () => {
  setQuestionnaireModalOpen(false);
  setTemplateData(null);
  setInitialResponseData(null);
  setQuestionnaireError(null);
};

  const getStatusChip = () => {
    if (!assignedTemplateId) {
      return <Chip label="Not Configured" color="default" />;
    }
    if (!latestResponse) {
      return <Chip label="Not Started" color="info" />;
    }
    switch (latestResponse.status) {
      case 'draft':
        return <Chip label="Draft in Progress" color="warning" />;
      case 'submitted_pii_review_pending':
        return <Chip label="Submitted - AI Processing Pending" color="secondary" />;
      case 'synthesis_complete':
        return <Chip label="Synthesis Complete" color="success" />;
      default:
        return <Chip label={latestResponse.status || "Unknown"} color="primary" />;
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Questionnaire</Typography>

      {!assignedTemplateId ? (
        <Typography>No questionnaire template has been assigned to this client.</Typography>
      ) : (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1"><strong>Status:</strong></Typography>
            {getStatusChip()}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
  <Tooltip title="Optionally email a secure link to the client for them to complete their questionnaire.">
    <Button
        variant="contained"
        onClick={handleOpenSendLinkModal}
        sx={{ textTransform: 'none', px: 3 }}
    >
        Email to Client
    </Button>
</Tooltip>

  {(!latestResponse) && (
    <Button
      variant="outlined"
      color="primary"
      onClick={() => handleOpenQuestionnaire('fill')}
      sx={{ textTransform: 'none', px: 3 }}
    >
      Start Questionnaire
    </Button>
  )}

  {latestResponse?.status === 'draft' && (
    <Button
      variant="outlined"
      color="primary"
      onClick={() => handleOpenQuestionnaire('continue_draft')}
      sx={{ textTransform: 'none', px: 3 }}
    >
      Continue Draft
    </Button>
  )}

  {latestResponse && latestResponse.status !== 'draft' && (
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => handleOpenQuestionnaire('fill')}
      sx={{ textTransform: 'none', px: 3 }}
    >
      Start New Version
    </Button>
  )}
</Box>
        </Box>
      )}

      <Dialog open={isSendLinkModalOpen} onClose={() => setSendLinkModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Questionnaire Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="CEO Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={ceoEmail}
            onChange={(e) => setCeoEmail(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Custom Message (Optional)"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: '0 24px 16px' }}>
          <Button onClick={() => setSendLinkModalOpen(false)} disabled={isSending}>Cancel</Button>
          <Button onClick={handleSendLink} variant="contained" disabled={isSending}>
            {isSending ? <CircularProgress size={24} /> : "Send Invite"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Questionnaire Modal */}
      <Dialog 
        open={questionnaireModalOpen} 
        onClose={handleCloseQuestionnaire}
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          {questionnaireMode === 'fill' && 'Fill Out Questionnaire'}
          {questionnaireMode === 'continue_draft' && 'Continue Draft Questionnaire'}
          {questionnaireMode === 'view' && 'View Submitted Questionnaire'}
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {questionnaireLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading questionnaire...</Typography>
            </Box>
          )}
          
          {questionnaireError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {questionnaireError}
            </Alert>
          )}
          
          {templateData && !questionnaireLoading && !questionnaireError && (
    <Box ref={scrollContainerRef} sx={{ height: '100%', overflow: 'auto', p: 2 }}>
      <PublicQuestionnaire
        template={templateData}
        onSubmitSuccess={handleQuestionnaireSubmit}
        isSubmitting={isSubmitting}
        mode={questionnaireMode}
        initialResponses={initialResponseData}
        user={user}
        clientId={clientId}
        scrollContainerRef={scrollContainerRef}
      />
    </Box>
  )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQuestionnaire}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CEOQuestionnaireModule;
